/**
 * Shared helpers for providers that talk to IDAAS OIDC token endpoints.
 *
 * Used by the redirect (`oauth2-pkce`), popup (`popup-login`), and iframe
 * (`iframe-login`) providers. Anything specific to a single flow stays in
 * that provider's file; only the wire-mapping helpers live here.
 */

import {
  type Client,
  type IdaasOidcProviderV1ResolveClientInfoResponse,
  oidcProtocolEndSessionPost,
  oidcProtocolExchangeToken,
  oidcProtocolRevoke,
  oidcProtocolUserInfoGet,
  oidcProviderServiceResolveClientInfo,
  type TokenResponse,
  type UserInfoResponse,
} from "@zero/idaas-api";
import { taggedAuthError } from "../auth-error";
import {
  buildUserInfo,
  decodeAccessTokenPayload,
  decodeIdTokenPayload,
  getTokenExpiry,
  normalizeProfileValue,
  tokenClaimsMatchProvider,
} from "../jwt";
import type { AuthError, AuthResult, LogoutOutcome, TokenSet } from "../types";

export interface TokenValidationContext {
  expectedIssuer: string;
  expectedClientId: string;
  expectedResource: string;
}

export function createTokenValidationContext(
  idaasBaseUrl: string,
  clientId: string,
  resource: string,
  configuredIssuer?: string,
): TokenValidationContext {
  return {
    expectedIssuer: new URL(configuredIssuer ?? idaasBaseUrl).origin,
    expectedClientId: clientId,
    expectedResource: resource,
  };
}

/**
 * Resolve realm + redirect URIs for an OAuth2 client.
 *
 * Throws if the response is missing `realmId` — every downstream call
 * (authorize URL, token exchange) needs it.
 */
export async function resolveClientInfo(
  client: Client,
  clientId: string,
  expectedRedirectUri: string,
): Promise<IdaasOidcProviderV1ResolveClientInfoResponse> {
  const response = await oidcProviderServiceResolveClientInfo({
    client,
    throwOnError: true,
    query: { clientId },
  });

  const { realmId, relyingPartyId, redirectUris, isDisabled } = response.data;
  if (!isBoundedIdentifier(realmId)) throw invalidProviderResponse("ResolveClientInfo returned an invalid realmId");
  if (!isBoundedIdentifier(relyingPartyId)) {
    throw invalidProviderResponse("ResolveClientInfo returned an invalid relyingPartyId");
  }
  if (typeof isDisabled !== "boolean") {
    throw invalidProviderResponse("ResolveClientInfo returned an invalid disabled state");
  }
  if (isDisabled) throw new Error("OAuth2 client is disabled");
  if (!Array.isArray(redirectUris) || redirectUris.length === 0 || redirectUris.length > 32) {
    throw invalidProviderResponse("ResolveClientInfo returned invalid redirect URIs");
  }
  const validatedRedirectUris = redirectUris.map(validateRedirectUri);
  if (!validatedRedirectUris.includes(expectedRedirectUri)) {
    throw new Error("Configured redirectUri is not registered for the OAuth2 client");
  }

  return { realmId, relyingPartyId, redirectUris: validatedRedirectUris, isDisabled };
}

/** Exchange an authorization code + PKCE verifier for a token set. */
export async function exchangeAuthorizationCode(
  client: Client,
  params: {
    clientId: string;
    redirectUri: string;
    code: string;
    codeVerifier: string;
  },
): Promise<TokenResponse> {
  const response = await oidcProtocolExchangeToken({
    client,
    throwOnError: true,
    body: {
      grant_type: "authorization_code",
      code: params.code,
      redirect_uri: params.redirectUri,
      client_id: params.clientId,
      code_verifier: params.codeVerifier,
    },
  });

  return response.data;
}

/** Fetch generated userinfo profile fields (email/name/picture). */
export async function fetchUserInfo(client: Client, accessToken: string | undefined): Promise<UserInfoResponse | null> {
  if (!accessToken) {
    return null;
  }

  const response = await oidcProtocolUserInfoGet({
    client,
    throwOnError: true,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

/** Run token revocation and OP end-session independently and report partial failure. */
export async function logoutRemote(
  client: Client,
  clientId: string,
  tokenSet: TokenSet | null,
): Promise<LogoutOutcome> {
  let revoke: LogoutOutcome["revoke"] = "skipped";
  if (tokenSet?.refreshToken) {
    try {
      await oidcProtocolRevoke({
        client,
        throwOnError: true,
        body: {
          token: tokenSet.refreshToken,
          token_type_hint: "refresh_token",
          client_id: clientId,
        },
      });
      revoke = "succeeded";
    } catch {
      revoke = "failed";
    }
  }

  let endSession: LogoutOutcome["endSession"] = "succeeded";
  try {
    await oidcProtocolEndSessionPost({
      client,
      throwOnError: true,
      body: {
        id_token_hint: tokenSet?.idToken ?? undefined,
        post_logout_redirect_uri: `${window.location.origin}/`,
        client_id: clientId,
      },
    });
  } catch {
    endSession = "failed";
  }

  return { revoke, endSession };
}

/** Convert generated-client refresh failures into retryable or terminal auth errors. */
export function toProviderRefreshError(error: unknown): Error & { authError: AuthError } {
  if (hasAuthError(error)) return error;

  const status = responseStatus(error);
  const retryable = status === undefined || status === 408 || status === 429 || status >= 500;
  const message = error instanceof Error ? error.message : "Token refresh failed";
  const authError: AuthError = retryable
    ? { kind: "network_error", message }
    : { kind: "refresh_failed", message, statusCode: status };
  return Object.assign(new Error(message), { authError });
}

function hasAuthError(error: unknown): error is Error & { authError: AuthError } {
  return (
    error instanceof Error && "authError" in error && typeof error.authError === "object" && error.authError !== null
  );
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const response = error.response;
  if (typeof response !== "object" || response === null || !("status" in response)) return undefined;
  return typeof response.status === "number" ? response.status : undefined;
}

/**
 * Map a token exchange response + optional userinfo into an `AuthResult`.
 *
 * Decodes the access token (and id token, if any) to build the `UserInfo`
 * roles/permissions/scope fields; userinfo provides email/name/picture when
 * IDAAS has them. Throws if the response is missing `access_token` — the
 * authenticated state requires a non-empty token by spec §3.2.
 */
export function tokenSetToAuthResult(tokenSet: TokenSet, userInfo: UserInfoResponse | null): AuthResult {
  const accessPayload = decodeAccessTokenPayload(tokenSet.accessToken);
  if (!accessPayload) {
    throw new Error("Failed to extract access token claims");
  }
  const idPayload = tokenSet.idToken ? decodeIdTokenPayload(tokenSet.idToken) : null;
  if (tokenSet.idToken && !idPayload) {
    throw new Error("Failed to extract ID token claims");
  }

  const user = buildUserInfo(idPayload, accessPayload);
  if (!user) {
    throw new Error("Failed to extract user info from token claims");
  }
  if (userInfo && userInfo.sub !== user.sub) {
    throw new Error("UserInfo subject does not match token subject");
  }

  return {
    user: {
      ...user,
      email: normalizeProfileValue(userInfo?.email, user.email, 320),
      name: normalizeProfileValue(userInfo?.name, user.name, 256),
      picture: normalizeProfileValue(userInfo?.picture, user.picture, 2048),
    },
    tokenSet,
  };
}

export function tokenResponseToAuthResult(
  raw: TokenResponse,
  userInfo: UserInfoResponse | null,
  context: TokenValidationContext,
): AuthResult {
  return tokenSetToAuthResult(tokenResponseToTokenSet(raw, context), userInfo);
}

/** Map a token exchange response into a `TokenSet` (no user extraction). */
export function tokenResponseToTokenSet(raw: TokenResponse, context: TokenValidationContext): TokenSet {
  if (typeof raw.access_token !== "string" || raw.access_token.length === 0) {
    throw new Error("Token response missing access_token");
  }
  if (new TextEncoder().encode(raw.access_token).length > MAX_TOKEN_VALUE_BYTES) {
    throw new Error("Token response access_token exceeds size limit");
  }
  if (raw.token_type !== "Bearer") {
    throw new Error("Token response contains invalid token_type");
  }
  if (!Number.isSafeInteger(raw.expires_in) || raw.expires_in <= 0) {
    throw new Error("Token response contains invalid expires_in");
  }
  const idToken = optionalTokenValue(raw.id_token, "id_token");
  const refreshToken = optionalTokenValue(raw.refresh_token, "refresh_token");
  return validateTokenSet(
    {
      accessToken: raw.access_token,
      refreshToken,
      idToken,
      expiresAt: 0,
      tokenType: raw.token_type,
    },
    context,
  );
}

/** Revalidate a token set restored from untrusted browser storage. */
export function validateTokenSet(
  tokenSet: TokenSet,
  context: TokenValidationContext,
  options?: { allowExpired?: boolean },
): TokenSet {
  if (tokenSet.tokenType !== "Bearer") throw new Error("Token set contains invalid token_type");
  if (typeof tokenSet.accessToken !== "string" || tokenSet.accessToken.length === 0) {
    throw new Error("Token set missing access token");
  }
  if (new TextEncoder().encode(tokenSet.accessToken).length > MAX_TOKEN_VALUE_BYTES) {
    throw new Error("Token set access token exceeds size limit");
  }
  const refreshToken = optionalStoredTokenValue(tokenSet.refreshToken, "refresh_token");
  const idToken = optionalStoredTokenValue(tokenSet.idToken, "id_token");
  const accessPayload = decodeAccessTokenPayload(tokenSet.accessToken);
  if (!accessPayload) throw new Error("Failed to extract access token claims");
  const idPayload = idToken ? decodeIdTokenPayload(idToken) : null;
  if (idToken && !idPayload) throw new Error("Failed to extract ID token claims");
  if (
    !tokenClaimsMatchProvider(
      accessPayload,
      idPayload,
      context.expectedIssuer,
      context.expectedClientId,
      context.expectedResource,
      Date.now(),
      options?.allowExpired ?? false,
    )
  ) {
    throw new Error("Token claims do not match the configured provider, client, or time window");
  }
  const expiresAt = getTokenExpiry(tokenSet.accessToken);
  if (expiresAt === null) throw new Error("Failed to extract access token expiry");
  return {
    accessToken: tokenSet.accessToken,
    refreshToken,
    idToken,
    expiresAt,
    tokenType: "Bearer",
  };
}

const MAX_TOKEN_VALUE_BYTES = 16_384;

function isBoundedIdentifier(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}

function validateRedirectUri(value: unknown): string {
  if (typeof value !== "string" || value.length === 0 || new TextEncoder().encode(value).length > 2_048) {
    throw invalidProviderResponse("ResolveClientInfo returned an invalid redirect URI");
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw invalidProviderResponse("ResolveClientInfo returned an invalid redirect URI");
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw invalidProviderResponse("ResolveClientInfo returned a redirect URI with an unsupported scheme");
  }
  return value;
}

function invalidProviderResponse(message: string): Error & { authError: AuthError } {
  return taggedAuthError({ kind: "invalid_provider_response", message });
}

function optionalTokenValue(value: unknown, field: string): string | null {
  if (value === undefined) return null;
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Token response contains invalid ${field}`);
  }
  if (new TextEncoder().encode(value).length > MAX_TOKEN_VALUE_BYTES) {
    throw new Error(`Token response ${field} exceeds size limit`);
  }
  return value;
}

function optionalStoredTokenValue(value: unknown, field: string): string | null {
  if (value === null) return null;
  return optionalTokenValue(value, field);
}
